/**
 * PinKebab — the one place the "pin this" interaction lives.
 *
 * A ⋮ overflow button that opens a small menu with Pin / Unpin, wired to the
 * shared pins store. Reused verbatim on every pinnable surface (agent + MCP
 * cards, agent table rows, …) so the affordance is identical everywhere.
 *
 * Right-click accelerator: the component forwards a `PinKebabHandle` exposing
 * `openMenu()`. A row/card attaches `onContextMenu` and calls it, so right-
 * click anywhere on the item opens this same menu (anchored to the kebab).
 *
 * The wrapping span stops click propagation so interacting with the kebab
 * never also triggers the card/row's own click (peek / navigate).
 */
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type ComponentType,
  type ReactElement,
} from 'react';
import { IconMore, IconPin, IconPinOff, type IconProps } from '@shared/icons';
import { type PinInput } from '@shared/pins';
import { usePins } from '@shared/pins/use-pins';
import { Button } from '../Button';
import { Popover } from '../Popover';
import { Menu, MenuItem, type MenuItemTone } from '../Menu';

export type PinKebabHandle = {
  /** Open the menu programmatically (used for the right-click accelerator). */
  openMenu: () => void;
};

/**
 * An extra action appended below Pin/Unpin (after a hairline divider), e.g. a
 * destructive "Delete" tucked into the same overflow menu. The menu closes
 * before `onSelect` runs, so callers can open a confirm dialog from it.
 */
export type PinKebabAction = {
  key: string;
  label: string;
  icon?: ComponentType<IconProps>;
  tone?: MenuItemTone;
  onSelect: () => void;
};

export type PinKebabProps = {
  /** The entity snapshot to pin/unpin. */
  item: PinInput;
  /** Extra classes for the wrapper span (e.g. overlay positioning). */
  className?: string;
  /** Extra menu actions rendered below Pin/Unpin (e.g. a role-gated Delete). */
  actions?: ReadonlyArray<PinKebabAction>;
};

export const PinKebab = forwardRef<PinKebabHandle, PinKebabProps>(function PinKebab(
  { item, className, actions = [] },
  ref
): ReactElement {
  const { isPinned, toggle } = usePins();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useImperativeHandle(ref, () => ({ openMenu: () => setOpen(true) }), []);

  const pinned = isPinned(item.kind, item.id);
  const label = `Actions for ${item.label}`;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- stopPropagation guard only (see file header), not itself interactive
    <span className={className} onClick={(e) => e.stopPropagation()}>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="xs"
        iconOnly
        leading={IconMore}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {label}
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        placement="bottom-end"
        width={196}
        ariaLabel={label}
      >
        <Menu ariaLabel={label}>
          <MenuItem
            icon={pinned ? IconPinOff : IconPin}
            onClick={() => {
              setOpen(false);
              toggle(item);
            }}
          >
            {pinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
          </MenuItem>
          {actions.length > 0 && (
            <div className="border-aq-border my-1 border-t" aria-hidden="true" />
          )}
          {actions.map((a) => (
            <MenuItem
              key={a.key}
              icon={a.icon}
              tone={a.tone}
              onClick={() => {
                setOpen(false);
                a.onSelect();
              }}
            >
              {a.label}
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </span>
  );
});
